import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Globe, Users, BarChart3, 
  FileText, Image, Link2, MessageSquare, Settings, Search,
  Copy, ExternalLink, Palette, Layout
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { copyToClipboard } from '../../utils/clipboard';
import { getImageUrl } from '../../utils/imageUrl';

// Comprehensive Page Templates
const TEMPLATES = [
  // Landing Page Templates
  { id: 'landing-product', name: 'Product Launch', icon: '🚀', category: 'landing', desc: 'Perfect for new product launches' },
  { id: 'landing-event', name: 'Event Page', icon: '🎉', category: 'landing', desc: 'Webinars, conferences, meetups' },
  { id: 'landing-webinar', name: 'Webinar Registration', icon: '📹', category: 'landing', desc: 'Capture webinar registrations' },
  { id: 'landing-app', name: 'App Download', icon: '📱', category: 'landing', desc: 'Mobile app promotion' },
  { id: 'landing-coming-soon', name: 'Coming Soon', icon: '⏳', category: 'landing', desc: 'Pre-launch teaser pages' },
  
  // Business Page Templates
  { id: 'business-about', name: 'About Us', icon: '💼', category: 'business', desc: 'Company story & team' },
  { id: 'business-services', name: 'Services', icon: '🔧', category: 'business', desc: 'Service offerings showcase' },
  { id: 'business-contact', name: 'Contact Page', icon: '📞', category: 'business', desc: 'Contact form & details' },
  { id: 'business-pricing', name: 'Pricing Table', icon: '💰', category: 'business', desc: 'Plans & pricing display' },
  { id: 'business-faq', name: 'FAQ Page', icon: '❓', category: 'business', desc: 'Frequently asked questions' },
  
  // Portfolio Templates
  { id: 'portfolio-personal', name: 'Personal Portfolio', icon: '🎨', category: 'portfolio', desc: 'Individual showcase' },
  { id: 'portfolio-agency', name: 'Agency Portfolio', icon: '🏢', category: 'portfolio', desc: 'Agency/team work' },
  { id: 'portfolio-photographer', name: 'Photography', icon: '📸', category: 'portfolio', desc: 'Photo gallery style' },
  { id: 'portfolio-creative', name: 'Creative Work', icon: '✨', category: 'portfolio', desc: 'Artists & designers' },
  { id: 'portfolio-minimal', name: 'Minimal Portfolio', icon: '⬜', category: 'portfolio', desc: 'Clean & simple' },
  
  // Sales Funnel Templates
  { id: 'funnel-lead', name: 'Lead Capture', icon: '🧲', category: 'funnel', desc: 'Email/contact collection' },
  { id: 'funnel-optin', name: 'Opt-in Page', icon: '📧', category: 'funnel', desc: 'Newsletter signup' },
  { id: 'funnel-squeeze', name: 'Squeeze Page', icon: '🎯', category: 'funnel', desc: 'High-conversion lead page' },
  { id: 'funnel-thankyou', name: 'Thank You Page', icon: '🙏', category: 'funnel', desc: 'Post-conversion page' },
  { id: 'funnel-sales', name: 'Sales Page', icon: '💵', category: 'funnel', desc: 'Long-form sales page' },
  { id: 'funnel-upsell', name: 'Upsell Page', icon: '📈', category: 'funnel', desc: 'Additional offer page' }
];

const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: '📋' },
  { id: 'landing', name: 'Landing Pages', icon: '🚀' },
  { id: 'business', name: 'Business Pages', icon: '💼' },
  { id: 'portfolio', name: 'Portfolio', icon: '🎨' },
  { id: 'funnel', name: 'Sales Funnels', icon: '🎯' }
];

const PageBuilder = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pages');
  const [templateCategory, setTemplateCategory] = useState('all');
  const [userSettings, setUserSettings] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  // Filter templates by category
  const filteredTemplates = templateCategory === 'all' 
    ? TEMPLATES 
    : TEMPLATES.filter(t => t.category === templateCategory);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pagesRes, statsRes, settingsRes, submissionsRes] = await Promise.all([
        api.get('/admin/pages/all'),
        api.get('/admin/pages/stats'),
        api.get('/admin/pages/user-settings'),
        api.get('/admin/pages/form-submissions')
      ]);
      setPages(pagesRes.data.pages || []);
      setStats(statsRes.data);
      setUserSettings(settingsRes.data || []);
      setFormSubmissions(submissionsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (pageId) => {
    try {
      const res = await api.post(`/admin/pages/toggle-publish/${pageId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };

  const deletePage = async (pageId) => {
    try {
      await api.delete(`/admin/pages/delete/${pageId}`);
      toast.success('Page deleted');
      setShowDeleteModal(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/p/${slug}`;
    const success = await copyToClipboard(url);
    if (success) {
      toast.success('Link copied!');
    } else {
      toast.error('Failed to copy link');
    }
  };

  const filteredPages = pages.filter(page => 
    page.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-builder-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layout className="w-7 h-7 text-primary" />
            Page Builder
          </h1>
          <p className="text-muted-foreground">Create and manage unlimited landing pages</p>
        </div>
        <button
          onClick={() => navigate('/admin/page-builder/new')}
          className="px-4 py-2 bg-primary text-black font-semibold rounded-xl hover:bg-primary/90 flex items-center gap-2"
          data-testid="create-page-btn"
        >
          <Plus className="w-5 h-5" />
          Create Page
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="glass-card p-4 text-center">
            <FileText className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_pages}</p>
            <p className="text-xs text-muted-foreground">Total Pages</p>
          </div>
          <div className="glass-card p-4 text-center">
            <Globe className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.published_pages}</p>
            <p className="text-xs text-muted-foreground">Published</p>
          </div>
          <div className="glass-card p-4 text-center">
            <Eye className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_views}</p>
            <p className="text-xs text-muted-foreground">Total Views</p>
          </div>
          <div className="glass-card p-4 text-center">
            <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.users_with_access}</p>
            <p className="text-xs text-muted-foreground">Users Enabled</p>
          </div>
          <div className="glass-card p-4 text-center">
            <MessageSquare className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_form_submissions}</p>
            <p className="text-xs text-muted-foreground">Form Leads</p>
          </div>
          <div className="glass-card p-4 text-center">
            <BarChart3 className="w-6 h-6 text-orange-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.admin_pages}/{stats.user_pages}</p>
            <p className="text-xs text-muted-foreground">Admin/User</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['pages', 'user-access', 'form-leads'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg font-medium transition-all ${
              activeTab === tab 
                ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-white'
            }`}
          >
            {tab === 'pages' && 'All Pages'}
            {tab === 'user-access' && 'User Access'}
            {tab === 'form-leads' && 'Form Leads'}
          </button>
        ))}
      </div>

      {/* Pages Tab */}
      {activeTab === 'pages' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl"
            />
          </div>

          {/* Pages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPages.map(page => (
              <div key={page.id} className="glass-card overflow-hidden group">
                {/* Cover Image */}
                <div 
                  className="h-32 bg-gradient-to-br from-primary/20 to-secondary/20 relative"
                  style={{
                    backgroundImage: page.cover_image ? `url(${getImageUrl(page.cover_image)})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="absolute top-2 right-2 flex gap-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      page.is_published ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {page.is_published ? 'Published' : 'Draft'}
                    </span>
                    <span className="px-2 py-1 text-xs rounded-full bg-white/10">
                      {TEMPLATES.find(t => t.id === page.template)?.icon || '📄'} {page.template}
                    </span>
                  </div>
                  {page.profile_image && (
                    <img 
                      src={getImageUrl(page.profile_image)} 
                      alt="" 
                      className="absolute -bottom-6 left-4 w-12 h-12 rounded-full border-2 border-background object-cover"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="p-4 pt-8">
                  <h3 className="font-bold text-lg truncate">{page.title}</h3>
                  <p className="text-sm text-muted-foreground truncate">{page.tagline || page.headline}</p>
                  
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Eye className="w-3 h-3" />
                    {page.view_count || 0} views
                    <span className="mx-1">•</span>
                    <span className={page.created_by === 'admin' ? 'text-primary' : 'text-purple-400'}>
                      {page.created_by === 'admin' ? 'Admin' : page.owner_username}
                    </span>
                  </div>

                  {/* URL */}
                  <div className="flex items-center gap-2 mt-3 p-2 bg-black/30 rounded-lg">
                    <code className="text-xs flex-1 truncate text-primary">/p/{page.slug}</code>
                    <button onClick={() => copyLink(page.slug)} className="p-1 hover:bg-white/10 rounded">
                      <Copy className="w-4 h-4" />
                    </button>
                    {page.is_published && (
                      <a href={`/p/${page.slug}`} target="_blank" rel="noreferrer" className="p-1 hover:bg-white/10 rounded">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => navigate(`/admin/page-builder/edit/${page.id}`)}
                      className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center justify-center gap-1"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => togglePublish(page.id)}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 ${
                        page.is_published 
                          ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30' 
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      }`}
                    >
                      {page.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setShowDeleteModal(page)}
                      className="px-3 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredPages.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No pages found</p>
              <button
                onClick={() => navigate('/admin/page-builder/new')}
                className="mt-4 px-4 py-2 bg-primary/20 text-primary rounded-lg"
              >
                Create your first page
              </button>
            </div>
          )}
        </div>
      )}

      {/* User Access Tab */}
      {activeTab === 'user-access' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Manage which users can create pages and their limits</p>
            <button
              onClick={() => navigate('/admin/page-builder/user-permissions')}
              className="px-4 py-2 bg-primary/20 text-primary rounded-lg flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Manage Permissions
            </button>
          </div>

          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">Max Pages</th>
                  <th className="text-left p-3">Templates</th>
                  <th className="text-left p-3">Features</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {userSettings.map(setting => (
                  <tr key={setting.user_id} className="border-t border-white/5">
                    <td className="p-3">
                      <p className="font-medium">{setting.user_name}</p>
                      <p className="text-xs text-muted-foreground">@{setting.user_username}</p>
                    </td>
                    <td className="p-3">{setting.permissions?.max_pages || 1}</td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        {(setting.permissions?.allowed_templates || ['business']).map(t => (
                          <span key={t} className="px-2 py-0.5 bg-white/10 rounded text-xs">
                            {TEMPLATES.find(temp => temp.id === t)?.icon} {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        {setting.permissions?.can_use_gallery && <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">Gallery</span>}
                        {setting.permissions?.can_use_video && <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">Video</span>}
                        {setting.permissions?.can_use_contact_form && <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">Forms</span>}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        setting.is_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {setting.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                ))}
                {userSettings.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      No users have page builder access yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Leads Tab */}
      {activeTab === 'form-leads' && (
        <div className="space-y-4">
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-3">Page</th>
                  <th className="text-left p-3">Submitted Data</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {formSubmissions.map(sub => (
                  <tr key={sub.id} className="border-t border-white/5">
                    <td className="p-3">
                      <p className="font-medium">{sub.page_title}</p>
                      <p className="text-xs text-muted-foreground">/p/{sub.page_slug}</p>
                    </td>
                    <td className="p-3">
                      <div className="text-sm space-y-1 max-w-md">
                        {Object.entries(sub.data || {}).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-muted-foreground">{key}:</span>
                            <span className="truncate">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {new Date(sub.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        sub.is_read ? 'bg-gray-500/20 text-gray-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {sub.is_read ? 'Read' : 'New'}
                      </span>
                    </td>
                  </tr>
                ))}
                {formSubmissions.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-muted-foreground">
                      No form submissions yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="glass-card p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Delete Page?</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete "<strong>{showDeleteModal.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="flex-1 px-4 py-2 bg-white/10 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => deletePage(showDeleteModal.id)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageBuilder;
